import type { WorkerModuleScorecard } from '@insolitum/types';
import { formatWorkerMetric, workerModuleState } from '@insolitum/utils';
import { AlertTriangle, Check, Loader2, Plus } from 'lucide-react';
import { useState } from 'react';

import { getModuleDef } from '../../../modules/registry';

/**
 * Rząd plakietek „gdzie ten człowiek jest mierzony".
 * ==================================================
 *
 * To jest jedyny element, który MUSI wyglądać tak samo na każdym poziomie —
 * na kafelku listy, w nagłówku kartoteki i w wynikach wyszukiwania. Stała
 * kolejność organów (z rejestru) sprawia, że sama POZYCJA plakietki niesie
 * informację: kadrowa uczy się, że trzecia od lewej to LexShift, i przestaje
 * czytać etykiety.
 *
 * Dotąd ten rząd był dekoracją: renderował `preferences.module_access`, którego
 * nie czytał żaden moduł, i pokazywał wyłącznie „ma dostęp / nie ma". Teraz
 * niesie cztery stany i każdy z nich jest innym zdaniem:
 *
 *   measured   — przypisany i mamy jego dane. Plakietka pełna, z liczbą.
 *   assigned   — przypisany, zero danych w oknie. Obrys. Luka we wdrożeniu.
 *   unassigned — nieprzypisany. Duch z „+", czyli przycisk przypisania.
 *   orphan     — MIERZONY BEZ PRZYPISANIA. Ostrzeżenie: pracuje w organie,
 *                do którego nikt go nie zapisał, więc na liście tego organu
 *                go nie ma.
 *
 * Kliknięcie przełącza przypisanie od razu, bez wchodzenia w modal — to jest
 * ten „prosty i łatwy dostęp z każdego miejsca". Wcześniej ta sama zmiana
 * kosztowała: Edytuj → właściwa zakładka modala → checkbox → Zapisz.
 */

interface ModuleAccessChipsProps {
  scorecards: WorkerModuleScorecard[];
  canManage: boolean;
  /** Zapis przypisania. Rzucony błąd cofa optymistyczną zmianę. */
  onToggle: (moduleSlug: string, enabled: boolean) => Promise<void>;
  size?: 'sm' | 'md';
  /** Ukryj organy, w których pracownik ani nie jest przypisany, ani mierzony. */
  hideUnassigned?: boolean;
}

export function ModuleAccessChips({
  scorecards,
  canManage,
  onToggle,
  size = 'sm',
  hideUnassigned = false,
}: ModuleAccessChipsProps) {
  const [pending, setPending] = useState<string | null>(null);

  const visible = hideUnassigned
    ? scorecards.filter((s) => s.has_access || s.is_measured)
    : scorecards;

  if (visible.length === 0) {
    return <p className="text-xs text-gray-500">Brak przypisań do modułów</p>;
  }

  const handleClick = async (slug: string, enabled: boolean) => {
    if (!canManage || pending) return;
    setPending(slug);
    try {
      await onToggle(slug, enabled);
    } finally {
      setPending(null);
    }
  };

  const pad = size === 'md' ? 'px-2.5 py-1.5 text-xs' : 'px-2 py-1 text-[11px]';
  const iconSize = size === 'md' ? 'w-3.5 h-3.5' : 'w-3 h-3';

  return (
    <div className="flex flex-wrap gap-1.5">
      {visible.map((card) => {
        const def = getModuleDef(card.module);
        if (!def) return null;
        const Icon = def.icon;
        const state = workerModuleState(card.has_access, card.is_measured);
        const busy = pending === card.module;

        // Liczba na plakietce to metryka, którą organ się przedstawia. Bez niej
        // plakietka mówi tylko „jest", a chodzi o to, żeby mówiła „i jak mu idzie".
        const headline = card.headline ? formatWorkerMetric(card.headline) : null;

        const label =
          state === 'measured'
            ? `${def.navName}: ${card.headline?.label ?? ''} ${headline ?? ''} — kliknij, aby odpiąć`
            : state === 'assigned'
              ? `${def.navName}: przypisany, brak danych w tym oknie — kliknij, aby odpiąć`
              : state === 'orphan'
                ? `${def.navName}: ma tu dane, ale NIE jest przypisany — przez to nie widać go na liście tego modułu. Kliknij, aby przypisać.`
                : `${def.navName}: nieprzypisany — kliknij, aby przypisać`;

        const classes =
          state === 'measured'
            ? `${def.color.bg} ${def.color.text} border ${def.color.border}`
            : state === 'assigned'
              ? `bg-transparent ${def.color.text} border ${def.color.border} opacity-70`
              : state === 'orphan'
                ? 'bg-amber-500/10 text-amber-300 border border-amber-500/40'
                : 'bg-gray-700/30 text-gray-500 border border-dashed border-gray-600';

        const Element = canManage ? 'button' : 'span';

        return (
          <Element
            key={card.module}
            {...(canManage
              ? {
                  type: 'button' as const,
                  onClick: (e: React.MouseEvent) => {
                    // Plakietki siedzą na klikalnym kafelku pracownika —
                    // bez tego przypisanie nawigowałoby do kartoteki.
                    e.stopPropagation();
                    e.preventDefault();
                    void handleClick(card.module, !card.has_access);
                  },
                  disabled: busy,
                }
              : {})}
            title={label}
            aria-label={label}
            className={`inline-flex items-center gap-1 rounded font-medium transition-all ${pad} ${classes} ${
              canManage ? 'hover:brightness-125 disabled:opacity-50' : ''
            }`}
          >
            {busy ? (
              <Loader2 className={`${iconSize} animate-spin`} />
            ) : state === 'orphan' ? (
              <AlertTriangle className={iconSize} />
            ) : state === 'unassigned' ? (
              <Plus className={iconSize} />
            ) : (
              <Icon className={iconSize} />
            )}
            <span>{def.navName}</span>
            {state === 'measured' && headline && (
              <span className="font-semibold tabular-nums">{headline}</span>
            )}
            {state === 'assigned' && <Check className={`${iconSize} opacity-40`} />}
          </Element>
        );
      })}
    </div>
  );
}
